import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { setMainNavigation } from "@insite/client-framework/Components/ShellHoleConnect";
import Logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCategoriesDataView,
    getCategoryDepthLoaded,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import {
    getLink,
    getPageLinkByNodeId,
    getPageLinkByPageType,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StylesContext } from "@insite/content-library/additionalStyles";
import MainNavigationItem from "@insite/content-library/Components/MainNavigationItem";
import NavigationDrawer from "@insite/content-library/Components/NavigationDrawer";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Hidden from "@insite/mobius/Hidden";
import { IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { MenuPresentationProps } from "@insite/mobius/Menu";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { trackAsImpersonation, trackGaNavBarClickEvent } from "@stoneage/App/Utilities/DataLayer";
import * as cssLinter from "css";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    links = "links",
    customCSS = "customCSS",
    externalLinks = "externalLinks",
    // openParentPages = "openParentPages",
    productsLink = "productsLink",
    externalLinkOverrideTitle = "externalLinkOverrideTitle",
    numberOfRowsPerColumn = "numberOfRowsPerColumn",
    wideMenuTheme = "wideMenuTheme",
}

const enum externalLinksField {
    link = "link",
    overrideTitle = "overrideTitle",
    categoryIds = "categoryIds",
}

interface ExternalLinkModel {
    fields: {
        link: string;
        overrideTitle?: string;
        categoryIds: string[];
    };
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        // [fields.openParentPages]: boolean;
        [fields.customCSS]: string;
        [fields.externalLinks]: ExternalLinkModel[];
        [fields.productsLink]: LinkFieldValue;
        [fields.externalLinkOverrideTitle]: string;
        [fields.numberOfRowsPerColumn]: number;
        [fields.wideMenuTheme]: boolean;
    };
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
}

interface LinkModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        linkType: "Link" | "MegaMenu";
        overrideTitle: string;
        title: string;
        maxDepth: number;
    };
}

export interface MappedLink {
    title: string;
    url: string;
    type?: string;
    excludeFromNavigation?: boolean;
    openInNewWindow?: boolean;
    maxDepth?: number;
    linkType?: "Link" | "MegaMenu";
    children?: MappedLink[];
    categoryId?: string;
    isProductsMenu?: boolean;
    isWideMenu?: boolean;
    isFeatured?: boolean;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const {
        fields: { links = [], externalLinks, productsLink, wideMenuTheme },
    } = ownProps;
    const mappedLinks = [];
    const categoryIdsToLoad: Dictionary<number> = {};

    const mappedExternalLinks: { [key: string]: { link: string; overrideTitle?: string; hasLink: boolean } } = {};
    externalLinks?.forEach(exLink => {
        const linkData = {
            link: exLink.fields.link,
            overrideTitle: exLink.fields.overrideTitle,
            hasLink: exLink.fields.link?.length > 0,
        };
        if (exLink.fields.categoryIds?.length) {
            exLink.fields.categoryIds.forEach(catId => {
                mappedExternalLinks[catId] = linkData;
            });
        }
    });

    let productsLinkField: any = { url: "", title: "" };
    if (productsLink) {
        productsLinkField = getLink(state, productsLink);
    }

    for (const link of links) {
        const {
            fields: { linkType, destination, openInNewWindow, overrideTitle, maxDepth, title },
        } = link;
        const { type, value } = destination;

        const depthToLoad = linkType === "MegaMenu" ? 3 : 1;

        let mappedLink: MappedLink | undefined;

        if (type === "Url") {
            mappedLink = {
                url: value,
                title,
            };
        } else if (type === "Page") {
            const readonlyPageLink = getPageLinkByNodeId(state, value);
            if (readonlyPageLink) {
                mappedLink = { ...readonlyPageLink };
                if (overrideTitle) {
                    mappedLink.title = overrideTitle;
                }
            }
        } else if (type === "Category") {
            mappedLink = setupCategoryLink(
                state,
                value,
                categoryIdsToLoad,
                depthToLoad,
                overrideTitle,
                state.data.categories.categoryIdsCalled,
                productsLinkField,
                wideMenuTheme,
            );

            if (
                mappedLink &&
                mappedLink.isProductsMenu &&
                mappedLink?.children?.length &&
                mappedLink.children[0].children
            ) {
                const productsMenu = mappedLink.children.find(cat => cat.title.toLowerCase() === "products");
                if (!productsMenu || !productsMenu?.children?.length) {
                    return;
                }

                // eslint-disable-next-line no-unsafe-optional-chaining
                for (const cat of productsMenu?.children) {
                    if (cat && cat.categoryId && mappedExternalLinks?.[cat.categoryId]?.hasLink) {
                        const link = {
                            title: mappedExternalLinks?.[cat.categoryId]?.overrideTitle || "View More",
                            url: mappedExternalLinks?.[cat.categoryId].link,
                            children: [],
                            openInNewWindow: true,
                        };
                        if (cat?.children) {
                            cat.children.push(link);
                        }
                    }
                }
            }
        }

        if (!mappedLink) {
            continue;
        }

        mappedLink.linkType = linkType;
        mappedLink.openInNewWindow = openInNewWindow;
        mappedLink.maxDepth = maxDepth;
        mappedLinks.push(mappedLink);
    }

    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";
    const orderSettings = getSettingsCollection(state).orderSettings;
    const session = getSession(state);
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;
    const isVmiAdmin = session.userRoles?.toLowerCase().indexOf("vmi_admin") !== -1;
    const isVmiUser = session.userRoles?.toLowerCase().indexOf("vmi_user") !== -1;

    return {
        links: mappedLinks,
        quickOrderLink: getPageLinkByPageType(state, "QuickOrderPage"),
        categoryIdsToLoad,
        categoryErrorStatusCodeById: state.data.categories.errorStatusCodeById,
        categoryIdsCalled: state.data.categories.categoryIdsCalled,
        displayChangeCustomerLink: session?.displayChangeCustomerLink,
        displayVmiNavigation:
            currentMode === "Vmi" && orderSettings.vmiEnabled && isAuthenticated && (isVmiAdmin || isVmiUser),
        displayModeSwitch: isAuthenticated && orderSettings.vmiEnabled && (isVmiAdmin || isVmiUser),
        currentMode,
        vmiPageLinks: isVmiAdmin
            ? [
                  getPageLinkByPageType(state, "VmiDashboardPage"),
                  getPageLinkByPageType(state, "VmiLocationsPage"),
                  getPageLinkByPageType(state, "VmiUsersPage"),
                  getPageLinkByPageType(state, "VmiBinsPage"),
                  getPageLinkByPageType(state, "VmiReportingPage"),
                  getPageLinkByPageType(state, "VmiOrderHistoryPage"),
              ]
            : isVmiUser
            ? [
                  getPageLinkByPageType(state, "VmiDashboardPage"),
                  getPageLinkByPageType(state, "VmiLocationsPage"),
                  getPageLinkByPageType(state, "VmiBinsPage"),
                  getPageLinkByPageType(state, "VmiReportingPage"),
                  getPageLinkByPageType(state, "VmiOrderHistoryPage"),
              ]
            : [],
        impersonation: trackAsImpersonation(state),
    };
};

const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: categoryId !== emptyGuid,
});

const setupCategoryLink = (
    state: ApplicationState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
    overrideTitle: string,
    categoryIdsCalled: string[],
    productsLinkField: any,
    wideMenuTheme: boolean,
) => {
    let mappedLink: MappedLink | undefined;

    if (!value) {
        return;
    }

    const depthLoaded = getCategoryDepthLoaded(state, value);
    const subCategoryIds = getSubCategoryIds(state, value);
    if (
        (depthLoaded < maxDepth || (!subCategoryIds && !categoryIdsCalled?.includes(value))) &&
        !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
    ) {
        categoryIdsToLoad[value] = maxDepth;
    } else {
        if (value === emptyGuid) {
            mappedLink = {
                url: productsLinkField?.url || "",
                title: overrideTitle || translate("Products"),
            };
            mappedLink.isProductsMenu = true;
            // SAINC-1522: https://xngage.atlassian.net/browse/SAINC-1522
            mappedLink.isWideMenu = wideMenuTheme;
        } else {
            const categoryState = getCategoryState(state, value);
            if (!categoryState.value) {
                if (
                    !categoryState.isLoading &&
                    !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
                ) {
                    categoryIdsToLoad[value] = maxDepth;
                }
                return;
            }
            mappedLink = {
                url: categoryState.value.path,
                title: overrideTitle || categoryState.value.shortDescription,
            };
        }

        loadSubCategories(mappedLink, subCategoryIds, 1, maxDepth, state, productsLinkField);
    }

    return mappedLink;
};

const loadSubCategories = (
    mappedLink: MappedLink,
    subCategoryIds: readonly string[] | undefined,
    currentDepth: number,
    maxDepth: number,
    state: ApplicationState,
    productsLinkField: any,
) => {
    if (currentDepth > maxDepth) {
        return;
    }
    mappedLink.children = [];

    if (!subCategoryIds) {
        return;
    }
    for (const subCategoryId of subCategoryIds) {
        const subCategory = getCategoryState(state, subCategoryId).value;
        if (!subCategory) {
            Logger.error(`Category ${subCategoryId} was not found in data state, excluding from navigation.`);
            continue;
        }

        // SAINC-866: Make Products Nav more dynamic - Only show Featured Categories
        if (subCategory && !subCategory.isFeatured) {
            // to not break this functionality, you need to exclude the Products link (level 0) from this logic
            if (subCategory.path !== productsLinkField?.url) {
                continue;
            }
        }

        const childLink: MappedLink = {
            url: subCategory.path,
            title: subCategory.shortDescription,
            children: [],
            categoryId: subCategoryId,
            isFeatured: subCategory.isFeatured,
        };

        mappedLink.children.push(childLink);

        loadSubCategories(childLink, subCategory.subCategoryIds, currentDepth + 1, maxDepth, state, productsLinkField);
    }
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface State {
    selectedLinkIndex?: number;
    mobileSearchModalIsOpen: boolean;
    logoLinksSearchElementPosition: any;
    headerProductsElementPosition: any;
}

export interface MainNavigationStyles {
    menuHeight: number;
    container?: InjectableCss;
    itemWrapper?: InjectableCss;
    quickOrderItemWrapper?: InjectableCss;
    menuItem?: InjectableCss;
    menuItemIcon?: IconProps;
    menuItemTypography?: TypographyPresentationProps;
    cascadingMenu?: MenuPresentationProps;
    megaMenu?: InjectableCss;
    megaMenuGridContainer?: InjectableCss;
    megaMenuGridItem?: InjectableCss;
    megaMenuHeading?: LinkPresentationProps;
    megaMenuLink?: LinkPresentationProps;
    mobileWrapper?: InjectableCss;
    compactFlyoutMenu?: InjectableCss;
    mobileMenuWrapper?: InjectableCss;
}

export const mainNavigationStyles: MainNavigationStyles = {
    menuHeight: 50,
    container: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    itemWrapper: {
        css: css`
            position: relative;
            overflow: visible;
            display: flex;
            align-items: center;
            height: 62px;
            ul {
                list-style: none;
            }
            &:hover span + div {
                display: block;
            }
            &:first-child {
                margin-left: -20px;
            }

            &:last-child {
                margin-right: -20px;
            }

            &:not(.wide-menu) {
                ul li {
                    margin-bottom: 15px;
                    a {
                        margin: 0;

                        &:hover,
                        &:focus {
                            span {
                                color: ${getColor("primary")};
                            }
                        }
                        span {
                            font-size: 13px;
                            font-weight: 500;
                            line-height: 14px;
                            color: ${getColor("text.main")};
                        }
                    }
                }
                .level2-ul {
                    margin-top: 15px;
                    li {
                        width: 100%;
                    }
                }

                .level3-ul {
                    margin-top: 8px;
                }

                &.products-menu {
                    //warthog products menu style goes here, not wide and is products menu
                    .level2-ul {
                        margin-top: 0;
                    }
                }
            }

            [class*="TooltipTriggerer"][aria-expanded="true"] {
                [class*="IconWrapper"] {
                    transform: rotate(-180deg);
                    transition: 0.2s;

                    svg {
                        color: ${getColor("secondary")} !important;
                    }
                }
            }

            ${({ link, logoLinksSearchElementPosition, headerProductsElementPosition, isWideMenu }: any) => {
                if (link && logoLinksSearchElementPosition && headerProductsElementPosition && isWideMenu) {
                    const left = logoLinksSearchElementPosition.x - headerProductsElementPosition.x;
                    return `
                        div[data-tippy-root] {
                            left: ${left}px !important;
                            transform: translate(0, 61px) !important;
                        }
                    `;
                }
                return `

                `;
            }}
        `,
    },
    megaMenuHeading: {
        css: css`
            font-weight: bold;
            margin-bottom: 8px;
        `,
    },
    menuItem: {
        css: css`
            display: flex;
            align-items: center;
            height: 35px;
            padding: 0 20px;

            &[aria-expanded="true"] {
                [class*="IconWrapper"] {
                    transform: rotate(-180deg);
                    transition: 0.2s;

                    svg {
                        color: ${getColor("secondary")} !important;
                    }
                }
            }

            &.toggle span {
                color: ${getColor("secondary")} !important;
            }
            &.toggle svg {
                color: ${getColor("common.ui6")} !important;
            }
        `,
    },
    menuItemIcon: {
        css: css`
            margin: -3px 0 0 5px;
            color: ${getColor("common.white")} !important;
            &&&& {
                &:hover,
                &:focus {
                    color: ${getColor("primary")} !important;
                }
            }
        `,
        size: 16,
        color: "common.white",
    },
    menuItemTypography: {
        weight: "bold",
        lineHeight: "18px",
        color: "common.white",
        css: css`
            color: ${getColor("common.white")} !important;
            &&&& {
                &:hover,
                &:focus {
                    color: ${getColor("primary")} !important;
                }
            }
        `,
    },
    megaMenu: {
        css: css``,
    },
    mobileWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    mobileMenuWrapper: {
        css: css`
            text-align: right;
        `,
    },
    megaMenuGridContainer: {
        css: css`
            &.mega-menu-grid-container {
                // level1 ul
                width: 100%;
                height: 100%;

                li.one-column {
                    width: 100%;
                    margin-bottom: 15px;
                    padding-right: 15px;

                    > a {
                        margin-bottom: 0;

                        &:hover,
                        &:focus {
                            span {
                                color: ${getColor("primary")};
                            }
                        }
                        span {
                            font-size: 13px;
                            font-weight: 500;
                            line-height: 14px;
                            color: ${getColor("text.main")};
                        }
                    }
                }

                li.multiple-columns {
                    // stoneage wide menu
                    ul.level2-ul {
                        display: flex;
                        flex-wrap: wrap;
                    }

                    li.level2-li {
                        min-width: 25%;
                        padding-right: 17px;
                        margin-bottom: 15px;

                        .level2-li-link-wrapper {
                            width: 100%;
                            padding-bottom: 11px;
                            margin-bottom: 18px;
                            border-bottom: 1px solid ${getColor("common.ui6")};
                            a {
                                span {
                                    font-size: 16px;
                                    font-weight: bold;
                                    line-height: 22px;
                                    color: ${getColor("secondary")};
                                }
                                &:hover,
                                &:focus {
                                    span {
                                        color: ${getColor("primary")};
                                    }
                                }
                            }
                        }
                    }

                    ul.level3-ul {
                        display: flex;
                        flex-wrap: wrap;
                        flex-direction: column;

                        ${({ numberOfRowsPerColumn }: any) => {
                            if (numberOfRowsPerColumn) {
                                return `
                                max-height: ${numberOfRowsPerColumn * 32}px;
                                `;
                            }
                            return `
                            max-height: 224px;
                            `;
                        }}

                        .level3-li {
                            margin-bottom: 14px;
                            a {
                                span {
                                    font-size: 13px;
                                    font-weight: 500;
                                    line-height: 14px;
                                    color: ${getColor("text.main")};
                                }

                                &:hover,
                                &:focus {
                                    span {
                                        color: ${getColor("primary")};
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
};

const defaultCustomCss = `.mobile-wrapper {
}

.mobile-menu-wrapper {
}

.menu-trigger-button {
}

.menu-trigger-button-icon {
}

.drawer {
}

.drawer-section-wrapper {
}

.currency-selector-wrapper {
}

.language-selector-wrapper {
}

.panel-menu {
}

.main-navigation-row {
}

.panel-section-wrapper {
}

.main-navigation-row-icon {
}

.user-row-text {
}

.punch-out-navigation-row-icon {
}

.punch-out-user-row-text {
}

.main-navigation-row-text {
}

.logo-links-row {
}

.logo-link-title {
}

.language-image {
}

.menu-row-icon {
}

.change-customer-row {
}

.change-customer-row-container {
}

.fulfillment-method-grid-item {
}

.addresses-grid-item {
}

.pick-up-address-grid-item {
}

.apply-button-grid-item {
}

.sign-out-row {
}

.sign-out-row-text {
}

.mobile-search-wrapper {
}

.mobile-search-button {
}

.mobile-search-button-icon {
}

.container {
}

.item-wrapper {
}

.cascading-menu {
}

.menu-item {
}

.mega-menu {
}

.mega-menu-grid-container {
}

.mega-menu-grid-item {
}

.mega-menu-heading-link {
}

.mega-menu-link {
}

.quick-order-item-wrapper {
}

.quick-order-link {
}

.vmi-page-link {
}

.mobile-search-modal {
}`;

export class MainNavigation extends React.Component<Props, State> {
    container = React.createRef<HTMLDivElement>();
    mobileSearchInput = React.createRef<HTMLInputElement>();
    declare context: React.ContextType<typeof StylesContext>;

    constructor(props: Props) {
        super(props);

        this.state = {
            mobileSearchModalIsOpen: false,
            logoLinksSearchElementPosition: undefined,
            headerProductsElementPosition: undefined,
        };
    }

    handleSizeChange = debounce(() => {
        if (typeof document !== "undefined" && document !== null) {
            const logoLinksSearchElementPosition =
                document && document?.getElementsByClassName("header_logo-links-search")[0]?.getBoundingClientRect();
            const headerProductsElementPosition =
                document && document?.getElementsByClassName("products-menu")[0]?.getBoundingClientRect();
            this.setState({
                logoLinksSearchElementPosition,
                headerProductsElementPosition,
            });
        }
    }, 50);

    componentDidMount() {
        window.addEventListener("resize", this.handleSizeChange);
        setTimeout(() => {
            this.handleSizeChange();
        }, 20);
    }

    UNSAFE_componentWillMount(): void {
        this.loadCategoriesIfNeeded();
        this.handleSizeChange();

        setMainNavigation({
            close: () => {
                this.setState({
                    selectedLinkIndex: undefined,
                });
            },
            openMenu: (index: number) => {
                this.setState({
                    selectedLinkIndex: index,
                });
            },
        });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleSizeChange);
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (!isEqual(this.props.categoryIdsToLoad, prevProps.categoryIdsToLoad)) {
            this.loadCategoriesIfNeeded(true);
        }
    }

    loadCategoriesIfNeeded(isComponentDidUpdate = false) {
        const { categoryIdsToLoad } = this.props;
        for (const categoryId of Object.keys(categoryIdsToLoad)) {
            if (!this.props.categoryErrorStatusCodeById?.[categoryId]) {
                const maxDepth = categoryIdsToLoad[categoryId];
                if (!this.props.categoryIdsCalled?.includes(categoryId) || isComponentDidUpdate) {
                    this.props.loadCategories(getParameter(categoryId, maxDepth));
                }
            }
        }
    }

    render() {
        const {
            links,
            quickOrderLink,
            fields: { customCSS, numberOfRowsPerColumn, wideMenuTheme },
            showFlyoutPricingToggle,
            showFlyoutInventoryToggle,
            vmiPageLinks,
            displayVmiNavigation,
            displayModeSwitch,
            currentMode,
            impersonation,
        } = this.props;
        const { selectedLinkIndex } = this.state;

        const mainNavContextStyles = this?.context?.styles?.mainNavigation ?? {};
        const styles = mergeToNew(mainNavigationStyles, mainNavContextStyles);

        const customCssWrapper = {
            css: css`
                ${customCSS}
            `,
        };

        const logoLinksSearchElementPosition = this.state.logoLinksSearchElementPosition;
        const headerProductsElementPosition = this.state.headerProductsElementPosition;

        return (
            <StyledWrapper {...customCssWrapper} role="navigation" aria-label="Primary">
                <StyledWrapper className="mobile-wrapper" {...styles.mobileWrapper}>
                    <Hidden className="mobile-menu-wrapper" above="md" {...styles.mobileMenuWrapper}>
                        <NavigationDrawer
                            links={links as any}
                            showQuickOrder={false}
                            quickOrderLink={quickOrderLink}
                            displayVmiNavigation={displayVmiNavigation}
                            vmiPageLinks={vmiPageLinks}
                            displayModeSwitch={displayModeSwitch}
                            currentMode={currentMode}
                            showFlyoutPricingToggle={showFlyoutPricingToggle}
                            showFlyoutInventoryToggle={showFlyoutInventoryToggle}
                            // allowOpenParentLinks={openParentPages}
                            {...({
                                wideMenuTheme,
                            } as any)}
                        />
                    </Hidden>
                </StyledWrapper>
                <Hidden below="lg">
                    {!displayVmiNavigation && (
                        <StyledWrapper className="container" {...styles.container} ref={this.container}>
                            {links
                                .filter(child => !child.excludeFromNavigation)
                                .map((link, index) => {
                                    let isProductsMenu = false;
                                    let isWideMenu = false;
                                    if (link && (link as any).isProductsMenu) {
                                        isProductsMenu = true;
                                    }
                                    if (link && (link as any).isWideMenu) {
                                        isWideMenu = true;
                                    }
                                    return (
                                        <StyledWrapper
                                            className={`item-wrapper  ${isWideMenu && "wide-menu"} ${
                                                isProductsMenu && "products-menu"
                                            }`}
                                            {...styles.itemWrapper}
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={index}
                                            {...({
                                                link,
                                                logoLinksSearchElementPosition,
                                                headerProductsElementPosition,
                                                isWideMenu,
                                            } as any)}
                                            aria-label="main-navigation-item"
                                        >
                                            <MainNavigationItem
                                                index={index}
                                                link={link as any}
                                                styles={styles}
                                                container={this.container}
                                                isOpen={index === selectedLinkIndex}
                                                displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                                                {...({
                                                    isProductsMenu,
                                                    isWideMenu,
                                                    numberOfRowsPerColumn,
                                                    wideMenuTheme,
                                                } as any)}
                                            />
                                        </StyledWrapper>
                                    );
                                })}
                        </StyledWrapper>
                    )}
                    {displayVmiNavigation && (
                        <StyledWrapper className="container" {...styles.container} ref={this.container}>
                            <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                {vmiPageLinks.map(
                                    link =>
                                        link && (
                                            <Link
                                                className="vmi-page-link"
                                                key={link.id}
                                                typographyProps={styles.menuItemTypography}
                                                color={styles.menuItemTypography?.color}
                                                {...styles.menuItem}
                                                id={link.id}
                                                href={link.url}
                                                onClick={() => {
                                                    // XNG-Change: track GA4
                                                    trackGaNavBarClickEvent(link.title, impersonation);
                                                }}
                                            >
                                                {link.title}
                                            </Link>
                                        ),
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </Hidden>
            </StyledWrapper>
        );
    }
}

MainNavigation.contextType = StylesContext;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const mainNavigation: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MainNavigation),
    definition: {
        group: "Header",
        icon: "magnifying-glass",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                onEditRow: (index: number, dispatch) => {
                    dispatch({
                        type: "SendToSite/OpenMainNavigation",
                        index,
                    });
                },
                onDoneEditingRow: dispatch => {
                    dispatch({
                        type: "SendToSite/CloseMainNavigation",
                    });
                },
                onLoad: dispatch => {
                    dispatch({
                        type: "SendToSite/CloseMainNavigation",
                    });
                },
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overrideTitle, title } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overrideTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overrideTitle) {
                            return overrideTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return title ?? value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "linkType",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "Link",
                        isRequired: true,
                        options: [
                            {
                                displayName: "Link",
                                value: "Link",
                            },
                            {
                                displayName: "Menu",
                                value: "MegaMenu",
                            },
                        ],
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowUrls: item => item.fields.linkType === "Link",
                        showCategoryRoot: item => true,
                    },
                    {
                        name: "overrideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields.destination.type !== "Url",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                        isVisible: item => item.fields.destination.type === "Url",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: (item: HasFields) => item.fields.linkType === "Link",
                    },
                ],
                tab: basicTab,
            },
            // {
            //     name: fields.openParentPages,
            //     displayName: "Open Parent Pages",
            //     tooltip:
            //         "When enabled, tapping on a text link in the flyout menu will directly open the linked webpage and require users to click the chevron to access child pages. If disabled, tapping any link in the flyout menu will navigate to a submenu of child pages.",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            //     tab: basicTab,
            // },
            {
                name: fields.externalLinks,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    return item.fields.link;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: externalLinksField.link,
                        displayName: "View More Link",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: externalLinksField.overrideTitle,
                        displayName: "View More Link Title",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: externalLinksField.categoryIds,
                        displayName: "Select a Category to add the View More link",
                        editorTemplate: "CategoriesField",
                        defaultValue: [],
                    },
                ],
            },
            {
                name: fields.productsLink,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.numberOfRowsPerColumn,
                displayName: "Number of L2s to show within the Products Nav",
                editorTemplate: "IntegerField",
                defaultValue: 7,
                fieldType: "General",
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: fields.wideMenuTheme,
                displayName: "Wide Menu Display Theme",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
                isVisible: item => BLUEPRINT_NAME === "warthog",
            },
        ],
    },
};

export default mainNavigation;
